/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import { Spacer } from "@cd2/components"
import { Fields } from "@cd2/forms"
import { P } from "@cd2/text/build/es6"
import React, { useState } from "react"
import { useAsyncEffect } from "../../hooks/useAsyncEffect"
import { IRecord } from "../../types/schema"

type IUser = IRecord<"skeem_users", "name" | "skeem_email"> & {
  branch?: { name: string }
}

export function MultiUserSelectList({
  value,
  onChange,
  label,
  disabled,
  branchId,
  accessibleBranchId,
  customerId,
  filter,
  showBranch,
}: {
  value?: (IUser | string)[]
  onChange(users: IUser[]): void
  label?: string
  disabled?: boolean
  branchId?: string
  accessibleBranchId?: string
  customerId?: string
  filter?: any
  showBranch?: boolean
}) {
  const skeem = window.skeem

  const [records, setRecords] = useState<IUser[]>([])
  const [selectedUsers, setSelectedUsers] = useState<IUser[]>([])

  function handleChange(val: IUser[]) {
    onChange(val)
  }

  function fetchUsers(filter: any[]) {
    return skeem.fetch2<IUser[]>({
      model: {
        name: "skeem_users",
        attributes: ["name", "skeem_email", { name: "branch", attributes: ["name"] }],
        filter: filter.length > 0 ? { and: filter } : undefined,
      },
    })
  }

  const ids = value?.map(x => (typeof x == "string" ? x : x.id)) ?? []
  useAsyncEffect(async () => {
    if (value) {
      const users = await fetchUsers([{ matches: { id: { or: ids } } }])
      setSelectedUsers(users)
    } else {
      setSelectedUsers([])
    }
  }, [JSON.stringify(ids)])

  useAsyncEffect(async () => {
    const filterData: any[] = [filter].filter(Boolean)
    if (branchId) {
      filterData.push({ matches: { branch: branchId } })
    }
    if (accessibleBranchId) {
      filterData.push({
        or: [
          { matches: { branch: accessibleBranchId } },
          {
            exists: {
              path: [
                { name: "accessibleBranches", filter: { matches: { id: accessibleBranchId } } },
              ],
            },
          },
        ],
      })
    }
    if (customerId) {
      filterData.push({ matches: { customer: customerId } })
    }
    const records = await fetchUsers(filterData)
    setRecords(records)
  }, [branchId, customerId])

  if (records.length === 0) {
    return <Fields.Select2 label={label} placeholder="No users available for select" disabled />
  }

  return (
    <Fields.Select2
      label={label}
      getOptionLabel={opt => opt.name}
      getOptionValue={opt => opt.id}
      onChange={handleChange}
      value={selectedUsers}
      disabled={disabled}
      options={records}
      filterOption={(opt: { data: IUser }, query: string) =>
        opt.data.skeem_email?.toLowerCase().includes(query ?? "") ||
        opt.data.name?.toLowerCase().includes(query ?? "")
      }
      components={{
        SingleValue: props => {
          const { innerProps, getStyles } = props
          const record = records.find(record => record.id === props.data.id)

          return (
            <div
              {...innerProps}
              style={{
                ...getStyles("singleValue", props),
              }}
            >
              <div>{record?.name}</div>
              <div
                css={css`
                  color: #333;
                  font-size: 12px;
                  font-weight: 500;
                `}
              >
                {record?.skeem_email}
              </div>
            </div>
          )
        },
        Option: props => {
          const { innerProps, innerRef, getStyles, isSelected } = props
          const record = records.find(record => record.id === props.data.id)

          return (
            <div
              ref={innerRef}
              {...innerProps}
              style={{
                ...getStyles("option", props),
              }}
            >
              {showBranch && (
                <React.Fragment>
                  <P blockSpacing={0} color={isSelected ? "white" : "#333"} size="small">
                    {record?.branch?.name}
                  </P>
                  <Spacer size={2} />
                </React.Fragment>
              )}
              <P blockSpacing={0} color={isSelected ? "white" : undefined}>
                {record?.name}
              </P>
              <P blockSpacing={0} color={isSelected ? "white" : "#333"} size="small">
                {record?.skeem_email}
              </P>
            </div>
          )
        },
      }}
      isMulti
    />
  )
}
